import { Box, Circle, HStack, IBoxProps, IStackProps } from 'native-base';
import React, { ReactNode, useMemo } from 'react';
import { Platform } from 'react-native';
import Swiper, { SwiperProps } from 'react-native-swiper';

import { ArrowCircle } from 'app/components/icons';
import { tID } from 'app/services/TestHelper';

export type CarouselProps = SwiperProps & {
  children: ReactNode;
};

// react-native-swiper uses setImmediate which is not available in the browser
// https://developer.mozilla.org/en-US/docs/Web/API/Window/setImmediate
if (Platform.OS === 'web' && !global.setImmediate) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: good enough for now
  global.setImmediate = function setImmediate(cb: any, ...args: any[]) {
    return setTimeout(() => cb(...args), 0);
  };
}

function Carousel({ children, ...props }: CarouselProps) {
  return (
    <Swiper
      autoplay={false}
      containerStyle={{
        height: '100%',
        width: '100%',
      }}
      nextButton={<NextButton />}
      prevButton={<PrevButton />}
      renderPagination={(index, total) => <CarouselPagination index={index} total={total} />}
      showsButtons={Platform.OS === 'web'}
      showsPagination
      testID={tID('Carousel')}
      {...props}
    >
      {children}
    </Swiper>
  );
}

type CarouselPaginationProps = IBoxProps & {
  index: number;
  total: number;
  _stack?: IStackProps;
};

function CarouselPagination({ index, total, _stack, ...props }: CarouselPaginationProps) {
  const items = useMemo(() => Array(total).fill(1), [total]);
  return (
    <Box bottom="15px" position="absolute" w="full" {...props}>
      <HStack bg="gray.800:alpha.80" mx="auto" px="3.5" py="2" rounded="full" space="2" {..._stack}>
        {items.map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Circle key={i} bg={index === i ? 'red.500' : 'white'} size="2" />
        ))}
      </HStack>
    </Box>
  );
}

export function NextButton() {
  return (
    <Box style={{ transform: [{ rotate: '180deg' }] }}>
      <ArrowCircle size={32} />
    </Box>
  );
}

export function PrevButton() {
  return <ArrowCircle size={32} />;
}

Carousel.Pagination = CarouselPagination;

export default Carousel;
